import React, { useState, useEffect } from 'react';

/**
 * Composant de gestion des Modalités de Paiement
 * Permet de créer, modifier et supprimer des modalités de paiement personnalisées
 */
const PaymentTermsManager = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    payment_delay_days: 30,
    discount_percentage: 0,
    discount_days: 0,
    is_default: false
  });

  // Charger les modalités
  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment-terms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTerms(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des modalités:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editingTerm 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/payment-terms/${editingTerm.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/payment-terms`;
      
      const response = await fetch(url, {
        method: editingTerm ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchTerms();
        resetForm();
        alert(editingTerm ? 'Modalité mise à jour !' : 'Modalité créée !');
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde de la modalité');
    }
  };

  const handleDelete = async (termId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette modalité ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payment-terms/${termId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        await fetchTerms();
        alert('Modalité supprimée');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (term) => {
    setEditingTerm(term);
    setFormData({
      name: term.name,
      description: term.description,
      payment_delay_days: term.payment_delay_days,
      discount_percentage: term.discount_percentage || 0,
      discount_days: term.discount_days || 0,
      is_default: term.is_default || false
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      payment_delay_days: 30,
      discount_percentage: 0,
      discount_days: 0,
      is_default: false
    });
    setEditingTerm(null);
    setShowForm(false);
  };

  // Templates prédéfinis pour aider à la création
  const presetTemplates = [
    { name: 'Paiement comptant', description: 'Paiement intégral avant la prestation', days: 0 },
    { name: 'Paiement à la livraison (COD)', description: 'Paiement à réception de la marchandise', days: 0 },
    { name: '30 jours fin de mois', description: 'Paiement à 30 jours fin de mois, dépôt de facture', days: 30 },
    { name: 'Acompte 50%', description: '50% à la commande, 50% à la livraison', days: 0 },
    { name: 'Acompte 70/30', description: '70% à la commande, 30% à la livraison', days: 0 },
  ];

  const applyPreset = (preset) => {
    setFormData({
      ...formData,
      name: preset.name,
      description: preset.description,
      payment_delay_days: preset.days
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="ml-3 text-gray-600">Chargement des modalités...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Modalités de Paiement</h3>
          <p className="text-sm text-gray-500">
            Gérez vos conditions de paiement réutilisables
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <span>+</span>
          <span>Nouvelle Modalité</span>
        </button>
      </div>

      {/* Formulaire de création/édition */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              {editingTerm ? 'Modifier la modalité' : 'Créer une nouvelle modalité'}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* Templates prédéfinis */}
          {!editingTerm && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Partir d'un modèle (optionnel)
              </label>
              <div className="flex flex-wrap gap-2">
                {presetTemplates.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la modalité *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Paiement 30 jours"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Délai de paiement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Délai de paiement (jours) *
                </label>
                <input
                  type="number"
                  value={formData.payment_delay_days}
                  onChange={(e) => setFormData({ ...formData, payment_delay_days: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
            </div>

            {/* Description / Conditions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description / Conditions *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Décrivez les conditions de paiement détaillées qui apparaîtront sur le document..."
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Remise si paiement anticipé */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remise si paiement anticipé (%)
                </label>
                <input
                  type="number"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="100"
                  step="0.5"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Jours pour bénéficier de la remise */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Délai pour bénéficier de la remise (jours)
                </label>
                <input
                  type="number"
                  value={formData.discount_days}
                  onChange={(e) => setFormData({ ...formData, discount_days: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            {/* Par défaut */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <label htmlFor="is_default" className="text-sm text-gray-700">
                Définir comme modalité par défaut
              </label>
            </div>

            {/* Boutons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {editingTerm ? 'Mettre à jour' : 'Créer la modalité'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des modalités */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-700">
            Modalités enregistrées ({terms.length})
          </h4>
        </div>

        {terms.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-3">💳</div>
            <p>Aucune modalité de paiement créée</p>
            <p className="text-sm mt-1">Créez votre première modalité pour l'utiliser dans vos documents</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {terms.map((term) => (
              <div key={term.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h5 className="font-medium text-gray-800">{term.name}</h5>
                      {term.is_default && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Par défaut
                        </span>
                      )}
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {term.payment_delay_days} jours
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{term.description}</p>
                    
                    {term.discount_percentage > 0 && (
                      <p className="text-xs text-orange-600 mt-2">
                        💰 {term.discount_percentage}% de remise si paiement sous {term.discount_days} jours
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(term)}
                      className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(term.id)}
                      className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aide */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-3">
          <span className="text-yellow-600 text-xl">💡</span>
          <div>
            <h5 className="font-medium text-yellow-800">Comment utiliser les modalités</h5>
            <ul className="text-sm text-yellow-700 mt-2 space-y-1">
              <li>• Les modalités sont disponibles lors de la création d'un devis ou facture</li>
              <li>• La modalité par défaut sera pré-sélectionnée automatiquement</li>
              <li>• Vous pouvez toujours personnaliser le texte avant d'enregistrer</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTermsManager;
